import { Plus } from '@orgus/ui/icons';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { PageActions } from '../components/shell/TopBar.tsx';
import { Button, Tooltip } from '@orgus/ui';
import '../features/new-session/command.ts';
import { NewSessionHost } from '../features/new-session/NewSessionHost.tsx';
import { openNewSession } from '../features/new-session/store.ts';
import { ActivityFeed } from '../features/overview/ActivityFeed.tsx';
import { CountStrip } from '../features/overview/CountStrip.tsx';
import { OnboardingChecklist } from '../features/onboarding/Checklist.tsx';
import '../features/onboarding/command.ts';
import { NeedsYou, useNeedsYou } from '../features/overview/NeedsYou.tsx';
import { OverviewSkeleton } from '../features/overview/OverviewSkeleton.tsx';
import { ProjectList } from '../features/overview/ProjectList.tsx';
import { LiveNow } from '../features/overview/LiveNow.tsx';
import { AgentField } from '../features/overview/AgentField.tsx';
import { FleetNow } from '../features/overview/FleetNow.tsx';
import { OrchestrationFlow } from '../features/overview/OrchestrationFlow.tsx';
import { LiveFrame } from '../features/overview/LiveFrame.tsx';
import { SystemStatus } from '../features/overview/SystemStatus.tsx';
import { SessionDrawerHost } from '../features/sessions/SessionDrawer.tsx';
import { useDateRange } from '../features/history/data.ts';
import { HistoryChart } from '../features/history/HistoryChart.tsx';
import { RangeControl } from '../features/history/RangeControl.tsx';

function Body() {
  const needs = useNeedsYou();
  const { range, setRange } = useDateRange();
  // The hero first, above the fold: one live frame with the vitals, the flow from projects to
  // main, and the outcomes beside it. Then every area's status at a glance, then the detail
  // (agents at work, what needs you, machines, projects) and the history. Below 1280 px it stacks.
  return (
    <div className="mx-auto flex w-full max-w-(--layout-width-dashboard) flex-col gap-6">
      <OnboardingChecklist />
      <LiveFrame>
        <CountStrip />
        {/* The flow over two thirds, the outcomes beside it as one column the height of the flow. */}
        <div className="grid gap-6 2xl:grid-cols-12">
          <OrchestrationFlow bare className="2xl:col-span-8" />
          {/* Its own height: never cut at the bottom, whatever the flow's height. */}
          <ActivityFeed bare className="2xl:col-span-4" />
        </div>
      </LiveFrame>
      <SystemStatus />
      {/* Then two columns that each flow on their own, so neither leaves a gap at any scale: the
          work (every agent at work, every agent as a square) and the where (what needs you, the
          machines, the projects). */}
      <div className="grid items-start gap-6 xl:grid-cols-12">
        <div className="flex min-w-0 flex-col gap-6 xl:col-span-8">
          <LiveNow />
          <AgentField />
        </div>
        <div className="flex min-w-0 flex-col gap-6 xl:col-span-4">
          <NeedsYou items={needs} />
          <FleetNow />
          <ProjectList compact />
        </div>
      </div>
      {/* Activity over the selected range (ADR 0109): sessions started, by agent. */}
      <div className="flex flex-col gap-4">
        <RangeControl title="History" range={range} setRange={setRange} />
        <HistoryChart range={range} title="Sessions started, by agent" metrics={['sessions.started']} groupBy="agent" noun="sessions started" height={160} />
      </div>
    </div>
  );
}

/** The one screen to leave open: live counts, the system in motion, what needs you (with what the watch found wrong), every project and the activity feed. */
export default function Overview() {
  return (
    <PageBody wide>
      <PageActions>
        <Tooltip content="Start Claude Code anywhere" shortcut={['n']}>
          <Button variant="secondary" size="sm" icon={<Plus className="text-accent-text" />} onClick={() => openNewSession()}>
            New session
          </Button>
        </Tooltip>
      </PageActions>
      <DataGate skeleton={<OverviewSkeleton />}>
        <Body />
        <SessionDrawerHost />
      </DataGate>
      <NewSessionHost />
    </PageBody>
  );
}
